import { useState, useCallback } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";

/**
 * Custom hook for city form validation using Zod
 * @returns {Object} Validation utilities and state
 */
export const useCityValidation = () => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  // Zod schema for city validation
  const createCitySchema = useCallback(() => {
    return z.object({
      name: z
        .string()
        .min(1, t("validation.cityNameRequired"))
        .min(2, t("validation.cityNameMinLength"))
        .trim(),
      nameArabic: z
        .string()
        .min(1, t("validation.cityArabicNameRequired"))
        .min(2, t("validation.cityArabicNameMinLength"))
        .trim(),
      deadline: z.string().min(1, t("validation.deadlineRequired")).trim(),
      deliveryFees: z
        .union([
          z.string().transform((val) => {
            const num = parseFloat(val);
            if (isNaN(num)) throw new Error("Invalid number");
            return num;
          }),
          z.number(),
        ])
        .refine((val) => val >= 0, {
          message: t("validation.deliveryFeesInvalid"),
        }),
      refusalFees: z
        .union([
          z.string().transform((val) => {
            if (val === "" || val === null || val === undefined) return 0;
            const num = parseFloat(val);
            if (isNaN(num)) throw new Error("Invalid number");
            return num;
          }),
          z.number(),
          z.undefined(),
          z.null(),
        ])
        .optional()
        .refine((val) => val === undefined || val === null || val >= 0, {
          message: t("validation.refusalFeesInvalid"),
        }),
      cancellationFees: z
        .union([
          z.string().transform((val) => {
            if (val === "" || val === null || val === undefined) return 0;
            const num = parseFloat(val);
            if (isNaN(num)) throw new Error("Invalid number");
            return num;
          }),
          z.number(),
          z.undefined(),
          z.null(),
        ])
        .optional()
        .refine((val) => val === undefined || val === null || val >= 0, {
          message: t("validation.cancellationFeesInvalid"),
        }),
      isFeesFixed: z.boolean().optional().default(false),
      hub: z.string().optional().default(""),
      zone: z.string().optional().default(""),
      delai_de_livraison: z.string().optional().default("24H"),
      jour: z.string().optional().default(""),
      ville: z.string().optional().default(""),
      sortie_par_semaine: z.string().optional().default(""),
    });
  }, [t]);

  // Validate a single field
  const validateField = useCallback(
    (name, value) => {
      const schema = createCitySchema();
      const fieldSchema = schema.shape[name];

      if (!fieldSchema) return null;

      try {
        fieldSchema.parse(value);
        // Remove error for this field if validation passes
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
        return null;
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errorMessage =
            error.errors[0]?.message || t("validation.invalidField");
          setErrors((prev) => ({
            ...prev,
            [name]: errorMessage,
          }));
          return errorMessage;
        }
        return t("validation.invalidField");
      }
    },
    [createCitySchema, t]
  );

  // Validate entire form
  const validateForm = useCallback(
    (formData) => {
      const schema = createCitySchema();

      try {
        const validatedData = schema.parse(formData);
        setErrors({});
        return { isValid: true, data: validatedData, errors: {} };
      } catch (error) {
        if (error instanceof z.ZodError) {
          const newErrors = {};
          error.errors.forEach((err) => {
            if (err.path[0]) {
              newErrors[err.path[0]] = err.message;
            }
          });
          setErrors(newErrors);
          return { isValid: false, data: null, errors: newErrors };
        }
        return {
          isValid: false,
          data: null,
          errors: { general: t("validation.generalError") },
        };
      }
    },
    [createCitySchema, t]
  );

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Clear specific field error
  const clearFieldError = useCallback((fieldName) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validateField,
    validateForm,
    clearErrors,
    clearFieldError,
    hasErrors: Object.keys(errors).length > 0,
    schema: createCitySchema(),
  };
};
